import FJNetworkStore from './FJNetworkStore';
import FJAppConstant from '../constants/FJAppConstant';
import FJTextToImageDataConfig from '../config/dataConfig/FJTextToImageDataConfig';
import { AI_PHOTO_EDITOR_MODELS } from '../config/dataConfig/FJAIPhotoEditorData';
import {
    AI_VIDEO_GENERATION_MODEL_LIST,
    AI_GENERATOR_VIDEO_TAB_TYPE,
} from '../config/dataConfig/FJAIVideoGeneratorData';
import { STYLE_LIST } from '../config/dataConfig/FJImageToImageData';
import { VIDEO_EFFECT_STYLE_LIST } from '../config/dataConfig/FJVideoEffectData';

// 私有变量
let _creditLimitInfo = null; // 存储完整的计费信息对象
let _loading = false; // 加载状态
let _error = null; // 错误信息
let _lastUpdated = null; // 最后更新时间
let _fetchPromise = null; // 当前正在进行的请求 Promise（用于避免重复请求）

// 工具名称到API接口名称的映射
export const AI_TOOL_NAME_MAP = {
    [FJAppConstant.TEXT_TO_IMAGE]: 'text-to-image',
    [FJAppConstant.AI_PHOTO_EDITOR]: 'photo-editor',
    [FJAppConstant.IMAGE_TO_IMAGE]: 'img-to-img',
    [FJAppConstant.TEXT_TO_VIDEO]: 'text-to-video',
    [FJAppConstant.IMAGE_TO_VIDEO]: 'image-to-video',
    'video-effect': 'video-effect',
};

/**
 * 模型计费信息单例 Store
 * 管理所有工具的模型计费信息，提供获取、刷新、查询等功能
 */
const FJModelCreditStore = {
    /**
     * 获取所有工具的计费信息（初始化）
     * @param {boolean} [forceRefresh=false] - 是否强制刷新，忽略缓存
     * @returns {Promise<Object|null>} 返回所有工具的计费信息对象，失败返回 null
     */
    fetch: async function (forceRefresh = false) {
        // 如果已有缓存且不强制刷新，直接返回
        if (!forceRefresh && _creditLimitInfo !== null && _lastUpdated !== null) {
            return _creditLimitInfo;
        }

        // 如果已有正在进行的请求，等待该请求完成
        if (_fetchPromise) {
            try {
                await _fetchPromise;
                return _creditLimitInfo;
            } catch (error) {
                return null;
            }
        }

        // 开始新的请求
        _loading = true;
        _error = null;

        _fetchPromise = (async () => {
            try {
                const { data, code } = await FJNetworkStore.getToolsCreditLimitInfo();

                if (code === 200 && data) {
                    _creditLimitInfo = data;
                    _lastUpdated = new Date().toISOString();
                    _error = null;
                    // 自动同步 credit 数据到各个工具的 config
                    await FJModelCreditStore.syncCreditToConfigs();
                    return _creditLimitInfo;
                } else {
                    _error = new Error(`Failed to fetch credit limit info: code ${code}`);
                    return null;
                }
            } catch (error) {
                _error = error;
                console.error('FJModelCreditStore.fetch error:', error);
                return null;
            } finally {
                _loading = false;
                _fetchPromise = null;
            }
        })();

        return await _fetchPromise;
    },

    /**
     * 强制刷新所有工具的计费信息
     * @returns {Promise<Object|null>} 返回所有工具的计费信息对象，失败返回 null
     */
    refresh: function () {
        return this.fetch(true);
    },

    /**
     * 同步 credit 数据到各个工具的 config
     * 将获取到的 credit response data 更新到各个工具的 data config 文件中
     */
    syncCreditToConfigs: async function () {
        if (!_creditLimitInfo) {
            console.debug('[FJModelCreditStore] syncCreditToConfigs: No credit info available, skipping sync.');
            return;
        }

        try {
            // 1. 更新 text-to-image 配置
            const textToImageCredit = _creditLimitInfo['text-to-image'];
            if (textToImageCredit && textToImageCredit.model) {
                // 更新 MODELS
                if (FJTextToImageDataConfig.MODELS) {
                    FJTextToImageDataConfig.MODELS.forEach(model => {
                        if (model.value && textToImageCredit.model[model.value]) {
                            const creditInfo = textToImageCredit.model[model.value];
                            const creditValue = creditInfo.value;

                            if (typeof creditValue === 'number') {
                                model.useCredit = creditValue;
                            } else if (creditValue && typeof creditValue === 'object' && !Array.isArray(creditValue)) {
                                // value 是 map，更新或添加 creditPriceLevels
                                model.creditPriceLevels = creditValue;
                                // 获取最低分辨率的价格用以显示
                                model.useCredit = creditValue[model.supportResolutions?.[0]?.value];
                            }

                            if (creditInfo.isNew !== undefined) {
                                model.isNew = creditInfo.isNew;
                            }
                        }
                    });
                }
            }

            // 2. 更新 photo-editor 配置
            const photoEditorCredit = _creditLimitInfo['photo-editor'];
            if (photoEditorCredit && photoEditorCredit.model && AI_PHOTO_EDITOR_MODELS) {
                AI_PHOTO_EDITOR_MODELS.forEach(model => {
                    if (model.value && photoEditorCredit.model[model.value]) {
                        const creditInfo = photoEditorCredit.model[model.value];
                        const creditValue = creditInfo.value;

                        if (typeof creditValue === 'number') {
                            model.useCredit = creditValue;
                        } else if (creditValue && typeof creditValue === 'object' && !Array.isArray(creditValue)) {
                            // value 是 map，更新或添加 creditPriceLevels
                            model.creditPriceLevels = creditValue;
                            // 获取最低分辨率的价格用以显示
                            model.useCredit = creditValue[model.supportResolutions?.[0]?.value];
                        }

                        if (creditInfo.isNew !== undefined) {
                            model.isNew = creditInfo.isNew;
                        }
                    }
                });
            }

            // 3. 更新 image-to-video/text-to-video 配置
            // 分别根据 text-to-video 和 image-to-video 的 creditInfo 更新，避免遗漏
            const imageToVideoCredit = _creditLimitInfo['image-to-video'];
            const textToVideoCredit = _creditLimitInfo['text-to-video'];

            if (AI_VIDEO_GENERATION_MODEL_LIST) {
                AI_VIDEO_GENERATION_MODEL_LIST.forEach(model => {
                    if (!model.value) return;

                    // 根据模型支持的模式，分别从对应的 creditInfo 中更新
                    const supportsImageToVideo =
                        model.supportedModes &&
                        model.supportedModes.includes(AI_GENERATOR_VIDEO_TAB_TYPE.AI_IMAGE_TO_VIDEO);
                    const supportsTextToVideo =
                        model.supportedModes &&
                        model.supportedModes.includes(AI_GENERATOR_VIDEO_TAB_TYPE.AI_TEXT_TO_VIDEO);

                    // 从 image-to-video 更新
                    if (supportsImageToVideo && imageToVideoCredit && imageToVideoCredit.model) {
                        const creditInfo = imageToVideoCredit.model[model.value];
                        if (creditInfo) {
                            const creditValue = creditInfo.value;

                            if (typeof creditValue === 'number') {
                                model.creditPrice = creditValue;
                            } else if (creditValue && typeof creditValue === 'object' && !Array.isArray(creditValue)) {
                                // value 是 map，更新或添加 creditPriceLevels
                                model.creditPriceLevels = creditValue;
                                // 获取最低分辨率的价格用以显示
                                model.creditPrice = creditValue[model.resolutions?.[0]?.value || '720p'];
                            }

                            if (creditInfo.isNew !== undefined) {
                                model.isNew = creditInfo.isNew;
                            }
                        }
                    }

                    // 从 text-to-video 更新
                    if (supportsTextToVideo && textToVideoCredit && textToVideoCredit.model) {
                        const creditInfo = textToVideoCredit.model[model.value];
                        if (creditInfo) {
                            const creditValue = creditInfo.value;

                            if (typeof creditValue === 'number') {
                                model.creditPrice = creditValue;
                            } else if (creditValue && typeof creditValue === 'object' && !Array.isArray(creditValue)) {
                                // value 是 map，更新或添加 creditPriceLevels
                                model.creditPriceLevels = creditValue;

                                // 获取最低分辨率的价格用以显示
                                model.creditPrice = creditValue[model.resolutions?.[0]?.value || '720p'];
                            }

                            if (creditInfo.isNew !== undefined) {
                                model.isNew = creditInfo.isNew;
                            }
                        }
                    }
                });
            }

            // 4. 更新 img-to-img 配置（根据 scene 的 value 更新 isPro）
            const imgToImgCredit = _creditLimitInfo['img-to-img'];
            if (imgToImgCredit && imgToImgCredit.scene && STYLE_LIST) {
                STYLE_LIST.forEach(category => {
                    if (category.list) {
                        category.list.forEach(style => {
                            if (style.value && imgToImgCredit.scene[style.value]) {
                                const creditValue = imgToImgCredit.scene[style.value].value;
                                if (typeof creditValue === 'number') {
                                    // >= 4 为 true，< 4 为 false
                                    style.isPro = creditValue >= 4;
                                }
                                style.isNew = imgToImgCredit.scene[style.value].isNew;
                            }
                        });
                    }
                });
            }

            // 5. 更新 video-effect 配置
            const videoEffectCredit = _creditLimitInfo['video-effect'];
            if (videoEffectCredit && videoEffectCredit.scene && VIDEO_EFFECT_STYLE_LIST) {
                VIDEO_EFFECT_STYLE_LIST.forEach(effect => {
                    if (effect.key && videoEffectCredit.scene[effect.key]) {
                        const creditValue = videoEffectCredit.scene[effect.key].value;
                        if (typeof creditValue === 'number') {
                            effect.useCredit = creditValue;
                        }
                    }
                });
            }
        } catch (error) {
            console.error('[FJModelCreditStore] syncCreditToConfigs error:', error);
        }
    },

    /**
     * 获取特定模型的积分信息
     * @param {string} toolName - 工具名称（如 'image-to-video'）
     * @param {string} modelName - 模型名称（如 'openai-sora-2'）
     * @returns {Object|null} 返回模型积分信息 { value, unit } 或 null
     */
    getModelCredit: function (toolName, modelName = 'normal') {
        if (!_creditLimitInfo || !toolName || !modelName) {
            return null;
        }

        const toolInfo = _creditLimitInfo[AI_TOOL_NAME_MAP[toolName]];
        if (!toolInfo) {
            return null;
        }

        // 检查是否有 model 字段
        if (toolInfo.model && toolInfo.model[modelName]) {
            return toolInfo.model[modelName];
        }

        // 如果没有 model 字段，可能是其他类型的计费信息（如 video-effect）
        // 直接查找 modelName 作为 key
        if (toolInfo[modelName]) {
            return toolInfo[modelName];
        }

        return null;
    },

    /**
     * 获取特定工具的所有模型积分
     * @param {string} toolName - 工具名称（如 'image-to-video'）
     * @returns {Object|null} 返回工具的所有计费信息，失败返回 null
     */
    getToolCredit: function (toolName) {
        if (!_creditLimitInfo || !toolName) {
            return null;
        }

        return _creditLimitInfo[AI_TOOL_NAME_MAP[toolName]] || null;
    },

    /**
     * 获取所有计费信息
     * @returns {Object|null} 返回所有计费信息，失败返回 null
     */
    getAllCreditInfo: function () {
        return _creditLimitInfo;
    },

    /**
     * 获取加载状态
     * @returns {boolean} 是否正在加载
     */
    isLoading: function () {
        return _loading;
    },

    /**
     * 获取错误信息
     * @returns {Error|null} 错误对象或 null
     */
    getError: function () {
        return _error;
    },

    /**
     * 获取最后更新时间
     * @returns {string|null} ISO 格式的时间字符串或 null
     */
    getLastUpdated: function () {
        return _lastUpdated;
    },

    /**
     * 清除缓存
     */
    clearCache: function () {
        _creditLimitInfo = null;
        _lastUpdated = null;
        _error = null;
        _fetchPromise = null;
    },
};

export default FJModelCreditStore;
